import type { ComponentType } from 'react'
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom'
import { RequireAuth, RequireRole } from './guards'
import { AuthLayout } from '@/layouts/AuthLayout'
import { UserLayout } from '@/layouts/UserLayout'
import { LawyerLayout } from '@/layouts/LawyerLayout'
import { captureInvitation } from '@/features/auth/invitation'
import { useAuthStore } from '@/stores/auth'

type PageModule<TExport extends string> = Record<TExport, ComponentType>

const routeFallbackElement = (
  <div className="text-on-surface-variant flex h-full min-h-40 items-center justify-center text-sm">
    加载中...
  </div>
)

function lazyRoute<TExport extends string>(
  loader: () => Promise<PageModule<TExport>>,
  exportName: TExport,
) {
  return {
    lazy: async () => {
      const module = await loader()
      return { Component: module[exportName] }
    },
    hydrateFallbackElement: routeFallbackElement,
  }
}

/**
 * 路由总表
 *
 * 前缀约定:
 *  /u/ai-doc  AI 文书入口 (公开, 未登录可浏览)
 *  /u/*       普通用户工作区 (路由守卫, 须登录)
 *  /lawyer/*  律师工作台 (路由守卫, 须有 lawyer 角色)
 *  /auth/*    微信回调落地页
 */
// 在首页重定向和路由守卫运行前捕获推广入口。
const invitationEntry = captureInvitation(
  new URL(window.location.href),
  useAuthStore.getState().isAuthenticated(),
)
if (invitationEntry) window.history.replaceState(window.history.state, '', invitationEntry)

export const router = createBrowserRouter([
  { path: '/', element: <Navigate to="/u/ai-doc" replace /> },

  {
    path: '/auth',
    element: <AuthLayout />,
    children: [
      {
        path: 'wechat-pay-result',
        ...lazyRoute(() => import('@/pages/auth/WechatPayResult'), 'WechatPayResultPage'),
      },
      {
        path: 'wechat-callback',
        ...lazyRoute(() => import('@/pages/auth/WechatCallback'), 'WechatCallbackPage'),
      },
    ],
  },

  {
    path: '/u',
    element: <UserLayout />,
    children: [
      { path: 'ai-doc', ...lazyRoute(() => import('@/pages/user/AiDoc'), 'AiDocPage') },
      {
        element: (
          <RequireAuth>
            <Outlet />
          </RequireAuth>
        ),
        children: [
          { index: true, ...lazyRoute(() => import('@/pages/user/Home'), 'UserHomePage') },
          {
            path: 'lawyers',
            ...lazyRoute(() => import('@/pages/user/LawyerList'), 'LawyerListPage'),
          },
          {
            path: 'lawyers/:id',
            ...lazyRoute(() => import('@/pages/user/LawyerProfile'), 'LawyerProfilePage'),
          },
          {
            path: 'lawyers/followed',
            ...lazyRoute(() => import('@/pages/user/FollowedLawyers'), 'FollowedLawyersPage'),
          },
          { path: 'orders', ...lazyRoute(() => import('@/pages/user/Orders'), 'OrdersPage') },
          { path: 'packages', ...lazyRoute(() => import('@/pages/user/Packages'), 'PackagesPage') },
          { path: 'consult', ...lazyRoute(() => import('@/pages/user/Consult'), 'ConsultPage') },
          {
            path: 'profile/edit',
            ...lazyRoute(() => import('@/pages/user/ProfileEdit'), 'ProfileEditPage'),
          },
          {
            path: 'settings',
            ...lazyRoute(() => import('@/pages/user/Settings'), 'SettingsPage'),
          },
        ],
      },
    ],
  },

  {
    path: '/lawyer/cert',
    element: (
      <RequireAuth>
        <Outlet />
      </RequireAuth>
    ),
    children: [
      {
        index: true,
        ...lazyRoute(() => import('@/pages/lawyer/CertWizard'), 'LawyerCertPage'),
      },
    ],
  },

  {
    path: '/lawyer',
    element: (
      <RequireRole role="lawyer">
        <LawyerLayout />
      </RequireRole>
    ),
    children: [
      {
        index: true,
        ...lazyRoute(() => import('@/pages/lawyer/Dashboard'), 'LawyerDashboardPage'),
      },
      {
        path: 'profile/edit',
        ...lazyRoute(() => import('@/pages/lawyer/ProfileEdit'), 'LawyerProfileEditPage'),
      },
      { path: 'ai-doc', ...lazyRoute(() => import('@/pages/user/AiDoc'), 'AiDocPage') },
      { path: 'consult', ...lazyRoute(() => import('@/pages/user/Consult'), 'ConsultPage') },
      { path: 'settings', ...lazyRoute(() => import('@/pages/user/Settings'), 'SettingsPage') },
    ],
  },

  { path: '/policy/:type', ...lazyRoute(() => import('@/pages/Policy'), 'PolicyPage') },
  {
    path: '/settings/notification',
    element: (
      <RequireAuth>
        <Outlet />
      </RequireAuth>
    ),
    children: [
      {
        index: true,
        ...lazyRoute(() => import('@/pages/settings/Notification'), 'NotificationSettingsPage'),
      },
    ],
  },

  { path: '*', ...lazyRoute(() => import('@/pages/NotFound'), 'NotFoundPage') },
])

// 同时支持站内导航进入新的推广链接。
let lastInvitationLocationKey = router.state.location.key
router.subscribe(({ location }) => {
  if (location.key === lastInvitationLocationKey) return
  lastInvitationLocationKey = location.key
  const cleanedPath = captureInvitation(
    new URL(`${location.pathname}${location.search}${location.hash}`, window.location.origin),
    useAuthStore.getState().isAuthenticated(),
  )
  if (cleanedPath) void router.navigate(cleanedPath, { replace: true })
})
